/**
 * authStore.ts
 *
 * Zustand store for authentication state management.
 * Handles user session, permissions, and auth operations.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  username?: string;
  avatar?: string;
  groups: string[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string | null, refreshToken?: string | null) => void;
  signIn: (user: User, accessToken: string, refreshToken?: string) => void;
  signOut: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  /**
   * Refresh the access token via POST /api/v1/auth/refresh using the current
   * (still-valid) token, and write the new token to BOTH the store and the
   * localStorage 'auth_token' key that most components/interceptors read.
   * Returns the new token on success, or null on failure (caller decides
   * whether to sign out). Does NOT sign out on its own.
   */
  refreshSession: () => Promise<string | null>;

  // Permission helpers
  hasGroup: (group: string) => boolean;
  hasAllGroups: (...groups: string[]) => boolean;
  hasAnyGroup: (...groups: string[]) => boolean;
  isAdmin: () => boolean;
}

// ============================================================================
// Group Constants
// ============================================================================

export const GROUPS = {
  EMS_USERS: 'ems_users',
  EMS_ADMINS: 'ems_admins',
  EMS_MANAGEMENT: 'ems_management',
  EMS_CONFIG: 'ems_config',
  EMS_DEVELOPERS: 'ems_developers',
  EMS_READONLY: 'ems_readonly',
} as const;

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true, // Start as loading to check for existing session

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken: refreshToken ?? get().refreshToken });
      },

      refreshSession: async () => {
        const current = get().accessToken || localStorage.getItem('auth_token');
        if (!current) return null;
        try {
          const res = await fetch('/api/v1/auth/refresh', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${current}`,
            },
          });
          if (!res.ok) return null;
          const data = await res.json();
          const newToken: string | undefined = data?.token;
          if (!newToken) return null;
          // Keep both stores in sync — components read either the raw
          // 'auth_token' key or the zustand accessToken.
          localStorage.setItem('auth_token', newToken);
          set({ accessToken: newToken, isAuthenticated: true });
          return newToken;
        } catch {
          return null;
        }
      },

      signIn: (user, accessToken, refreshToken) => {
        set({
          user,
          accessToken,
          refreshToken: refreshToken ?? null,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      signOut: async () => {
        // Clear raw token from localStorage (used by apiClient interceptor and WebSocket URLs)
        localStorage.removeItem('auth_token');

        // Clear all auth state (Zustand persist will update localStorage['ems-auth-v1'])
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });

        window.location.href = '/login';
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      // Permission helpers
      hasGroup: (group) => {
        const { user } = get();
        return user?.groups?.includes(group) ?? false;
      },

      hasAllGroups: (...groups) => {
        const { user } = get();
        if (!user?.groups) return false;
        return groups.every((g) => user.groups.includes(g));
      },

      hasAnyGroup: (...groups) => {
        const { user } = get();
        if (!user?.groups) return false;
        return groups.some((g) => user.groups.includes(g));
      },

      isAdmin: () => {
        const { hasGroup } = get();
        return hasGroup(GROUPS.EMS_ADMINS);
      },
    }),
    {
      name: 'ems-auth-v1',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Mark loading as false after rehydration
        if (state) {
          state.setLoading(false);
        }
      },
    }
  )
);

export default useAuthStore;